import React, { useState } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ZURIYA_DOMAIN = 'zuriya.co.uk';

export function getShareUrl(path) {
  return `https://${ZURIYA_DOMAIN}${path}`;
}

export default function ShareButton({ path, title, description, className = '' }) {
  const [showPanel, setShowPanel] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = getShareUrl(path);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard API failed, using fallback:', err);
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'Check this out on Zuriya',
          text: description || '',
          url: shareUrl
        });
      } catch (e) {
        if (e.name !== 'AbortError') copyLink();
      }
    } else {
      setShowPanel(true);
    }
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`${title || 'Check this out'} - ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareToX = () => {
    const text = encodeURIComponent(`${title || 'Check this out'}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={nativeShare}
        className="gap-2"
        data-testid="share-button"
      >
        <Share2 className="h-4 w-4" />
        Share
      </Button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-[#E6E4DD] z-50 p-4 animate-fadeIn" data-testid="share-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-[#1A1A1A] text-sm">Share</span>
              <button onClick={() => setShowPanel(false)} className="text-stone-400 hover:text-stone-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Copy Link */}
            <div className="flex items-center gap-2 p-2 bg-[#F9F6F0] rounded-lg mb-3">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent text-xs text-stone-600 outline-none font-mono truncate"
                data-testid="share-url-display"
              />
              <button
                onClick={copyLink}
                className="flex-shrink-0 p-1.5 hover:bg-white rounded transition-colors"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-stone-500" />}
              </button>
            </div>

            {/* Social buttons */}
            <div className="flex gap-2">
              <button
                onClick={shareToWhatsApp}
                className="flex-1 py-2 px-3 bg-[#25D366] text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
                data-testid="share-whatsapp"
              >
                WhatsApp
              </button>
              <button
                onClick={shareToX}
                className="flex-1 py-2 px-3 bg-[#1A1A1A] text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
                data-testid="share-x"
              >
                X
              </button>
              <button
                onClick={shareToFacebook}
                className="flex-1 py-2 px-3 bg-[#1877F2] text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
                data-testid="share-facebook"
              >
                Facebook
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
